import { useState } from "react";
import { Calculator } from "lucide-react";
import { fmtMoney } from "../lib/stages";

export default function RevenueCalculator({ stats }) {
  const [leads, setLeads] = useState(100);
  const [monthly, setMonthly] = useState(stats.avg_monthly_value || 2000);
  const [targetRevenue, setTargetRevenue] = useState(50000);

  const r = stats.rates;
  const leadToWon = (r.lead_to_won || 0) / 100;
  const leadToCall = (r.lead_to_call || 0) / 100;

  const projCalls = Math.round(leads * leadToCall);
  const projCloses = leads * leadToWon;
  const projMrr = projCloses * (monthly || 0);
  const neededLeads = leadToWon > 0 && monthly > 0 ? Math.ceil(targetRevenue / (leadToWon * monthly)) : null;

  const inputCls =
    "w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-white font-mono-tech focus:outline-none focus:border-[#4690d9]";

  return (
    <div className="glass-panel p-6 fade-up" data-testid="revenue-calculator">
      <p className="eyebrow mb-1 flex items-center gap-2">
        <Calculator size={12} /> Reverse-Math Engine
      </p>
      <h2 className="text-lg font-bold text-white mb-5">MRR Forecast Calculator</h2>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="text-xs text-slate-400 block mb-1">Leads in</label>
          <input data-testid="calc-leads-input" type="number" min="0" value={leads} onChange={(e) => setLeads(Number(e.target.value))} className={inputCls} />
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1">Monthly value ($/mo)</label>
          <input data-testid="calc-monthly-input" type="number" min="0" value={monthly} onChange={(e) => setMonthly(Number(e.target.value))} className={inputCls} />
        </div>
      </div>

      <div className="rounded-lg bg-black/25 border border-white/[0.08] p-4 mb-4" data-testid="calc-projection">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">Calls</p>
            <p className="text-xl font-bold font-mono-tech text-blue-300">{projCalls}</p>
          </div>
          <div>
            <p className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">Closes</p>
            <p className="text-xl font-bold font-mono-tech text-emerald-300">{projCloses.toFixed(1)}</p>
          </div>
          <div>
            <p className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">MRR</p>
            <p className="text-xl font-bold font-mono-tech text-white">{fmtMoney(projMrr)}</p>
          </div>
        </div>
      </div>

      <div className="border-t border-white/[0.08] pt-4">
        <label className="text-xs text-slate-400 block mb-1">Target MRR ($/mo) → leads required</label>
        <div className="flex items-center gap-3">
          <input data-testid="calc-target-revenue-input" type="number" min="0" value={targetRevenue} onChange={(e) => setTargetRevenue(Number(e.target.value))} className={inputCls} />
          <div className="shrink-0 text-right" data-testid="calc-needed-leads">
            <p className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">Leads needed</p>
            <p className="text-xl font-bold font-mono-tech text-[#9cc6f0]">{neededLeads ?? "—"}</p>
          </div>
        </div>
        {!neededLeads && (
          <p className="text-[11px] text-amber-400/80 mt-2">Close at least one deal (or set a monthly value) to unlock reverse math.</p>
        )}
      </div>
    </div>
  );
}
